import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import TimePicker from '../../../components/ui/TimePicker';
import DatePicker from '../../../components/ui/DatePicker';

const ServiceDetailsForm = ({ formData, setFormData, errors }) => {
  const [showPickupTimePicker, setShowPickupTimePicker] = useState(false);
  const [showReturnTimePicker, setShowReturnTimePicker] = useState(false);
  const [showPickupDatePicker, setShowPickupDatePicker] = useState(false);
  const [showReturnDatePicker, setShowReturnDatePicker] = useState(false);

  const serviceOptions = [
    { value: 'oneway', label: 'One Way Trip', description: 'Single journey to destination' },
    { value: 'roundtrip', label: 'Round Trip', description: 'Return journey included' },
    { value: 'local', label: 'Local Tour', description: 'City sightseeing' },
    { value: 'tempo', label: 'Tempo Traveller', description: 'Group travel (12-17 seats)' },
    { value: 'urbania', label: 'Force Urbania', description: 'Luxury travel (10-13 seats)' }
  ];

  const vehicleOptions = [
    { value: 'sedan', label: 'Sedan', description: '4 passengers' },
    { value: 'suv', label: 'SUV', description: '6-7 passengers' },
    { value: 'innova', label: 'Innova Crysta', description: '6-7 passengers' },
    { value: 'tempo', label: 'Tempo Traveller', description: '12-17 passengers' },
    { value: 'urbania', label: 'Force Urbania', description: '10-13 passengers' }
  ];

  const popularRoutes = [
    { value: 'amritsar-delhi', label: 'Amritsar → Delhi' },
    { value: 'chandigarh-manali', label: 'Chandigarh → Manali' },
    { value: 'ludhiana-shimla', label: 'Ludhiana → Shimla' },
    { value: 'jalandhar-dharamshala', label: 'Jalandhar → Dharamshala' },
    { value: 'delhi-haridwar', label: 'Delhi → Haridwar' },
    { value: 'custom', label: 'Custom Route' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePickupTimeSelect = (time) => {
    handleInputChange('pickupTime', time);
  };

  const handleReturnTimeSelect = (time) => {
    handleInputChange('returnTime', time);
  };

  return (
    <div className="bg-card rounded-lg p-4 md:p-6 lg:p-8 shadow-sm space-y-4 md:space-y-6">
      <div className="flex items-center gap-3 pb-4 border-b border-border">
        <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-primary/10 flex items-center justify-center">
          <Icon name="Car" size={20} className="text-primary" />
        </div>
        <div>
          <h2 className="text-lg md:text-xl lg:text-2xl font-heading font-bold text-foreground">
            Service Details
          </h2>
          <p className="text-sm text-muted-foreground">
            Select your journey preferences
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        <Select
          label="Service Type"
          description="Choose your travel service"
          options={serviceOptions}
          value={formData?.serviceType}
          onChange={(value) => handleInputChange('serviceType', value)}
          error={errors?.serviceType}
          required
          searchable
        />

        <Select
          label="Vehicle Type"
          description="Select preferred vehicle"
          options={vehicleOptions}
          value={formData?.vehicleType}
          onChange={(value) => handleInputChange('vehicleType', value)}
          error={errors?.vehicleType}
          required
          searchable
        />
      </div>
      <Select
        label="Select Route"
        description="Choose from popular routes or enter custom"
        options={popularRoutes}
        value={formData?.route}
        onChange={(value) => handleInputChange('route', value)}
        error={errors?.route}
        required
        searchable
      />
      {formData?.route === 'custom' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 p-4 bg-muted/50 rounded-lg">
          <Input
            label="Pickup Location"
            type="text"
            placeholder="Enter pickup city"
            value={formData?.pickupLocation}
            onChange={(e) => handleInputChange('pickupLocation', e?.target?.value)}
            error={errors?.pickupLocation}
            required
          />

          <Input
            label="Drop Location"
            type="text"
            placeholder="Enter destination city"
            value={formData?.dropLocation}
            onChange={(e) => handleInputChange('dropLocation', e?.target?.value)}
            error={errors?.dropLocation}
            required
          />
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        <Input
          label="Pickup Date"
          value={formData?.pickupDate}
          onChange={(e) => handleInputChange('pickupDate', e?.target?.value)}
          error={errors?.pickupDate}
          readOnly
          onClick={() => setShowPickupDatePicker(true)}
          className="cursor-pointer"
        />

        <div>
          <Input
            label="Pickup Time"
            type="text"
            value={formData?.pickupTime}
            onClick={() => setShowPickupTimePicker(true)}
            onChange={() => {}}
            error={errors?.pickupTime}
            placeholder="Select time"
            required
            readOnly
            className="cursor-pointer"
          />
        </div>
      </div>
      {formData?.serviceType === 'roundtrip' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 p-4 bg-primary/5 rounded-lg border border-primary/20">
          <Input
            label="Return Date"
            value={formData?.returnDate}
            onChange={(e) => handleInputChange('returnDate', e?.target?.value)}
            error={errors?.returnDate}
            readOnly
            onClick={() => setShowReturnDatePicker(true)}
            className="cursor-pointer"
          />

          <div>
            <Input
              label="Return Time"
              type="text"
              value={formData?.returnTime}
              onClick={() => setShowReturnTimePicker(true)}
              onChange={() => {}}
              error={errors?.returnTime}
              placeholder="Select time"
              required
              readOnly
              className="cursor-pointer"
            />
          </div>
        </div>
      )}
      <Input
        label="Special Requirements (Optional)"
        type="text"
        placeholder="Any special requests or requirements"
        value={formData?.specialRequirements}
        onChange={(e) => handleInputChange('specialRequirements', e?.target?.value)}
        description="Child seat, extra luggage space, etc."
      />

      <TimePicker
        isOpen={showPickupTimePicker}
        onClose={() => setShowPickupTimePicker(false)}
        onSelect={handlePickupTimeSelect}
        initialTime={formData?.pickupTime}
      />

      <TimePicker
        isOpen={showReturnTimePicker}
        onClose={() => setShowReturnTimePicker(false)}
        onSelect={handleReturnTimeSelect}
        initialTime={formData?.returnTime}
      />

      <DatePicker
        isOpen={showPickupDatePicker}
        onClose={() => setShowPickupDatePicker(false)}
        onSelect={(date) => {
          handleInputChange('pickupDate', date);
          setShowPickupDatePicker(false);
        }}
        initialDate={formData?.pickupDate}
        minDate={new Date()?.toISOString()?.split('T')?.[0]}
      />

      <DatePicker
        isOpen={showReturnDatePicker}
        onClose={() => setShowReturnDatePicker(false)}
        onSelect={(date) => {
          handleInputChange('returnDate', date);
          setShowReturnDatePicker(false);
        }}
        initialDate={formData?.returnDate}
        minDate={formData?.pickupDate || new Date()?.toISOString()?.split('T')?.[0]}
      />
    </div>
  );
};

export default ServiceDetailsForm;